import { useEffect } from 'react';
import { useMarkersContext } from '../context/markers';
import {
  useStationMarkersLayer,
  useFoundedPlaceLayer,
  useStationPriceMarkerLayers,
  useSelectedStationOnRouteLayer,
} from 'features/stations';
import { useCarMarkersLayer, useCarNumberMarkersLayer } from 'features/cars';
import { useStationsDisplay } from 'entities/stations';
import { EMPTY_ARRAY } from 'common/constants';

import { MarkerLocations, MarkerHandlers } from './useMarkers';
import {
  useClientFuelHistoryEventsMarkersLayer,
  useClientFuelHistoryEventsTitleMarkersLayer,
  useStopTruckHistoryEventsMarkerLayer,
  useUntrackHistoryEventsMarkerLayer,
} from 'features/user/ClientHistoryEvents';
import { useUserCurrentLocationMarkersLayer } from 'features/myFleet';
import { useHistoryPathPointMarkerLayer } from 'features/navigation/NavigationHistory/hooks';
import { useRoutesContext } from 'common/context/routes';
import { useTollMarkerLayer } from 'features/tolls/tollMarker';
import { useUserGeoMarkersLayer } from 'features/user/userGeoPositionMarker';
import { useSelectedStationOnRoutePricesLayer } from './useSelectedStationOnRoutePricesLayer';

const useUpdateLayers = (
  { stations = EMPTY_ARRAY, extraStations, layers = EMPTY_ARRAY }: MarkerLocations,
  { onMarkerClick }: MarkerHandlers = {}
) => {
  const stationsDisplay = useStationsDisplay();
  const { updateLayers } = useMarkersContext();
  const { activeRoute } = useRoutesContext();

  // Autos
  const carMarkersLayer = useCarMarkersLayer();
  const carNumberMarkersLayer = useCarNumberMarkersLayer();

  // history
  const clientHistoryEventsMarkersLayer = useClientFuelHistoryEventsMarkersLayer();
  const clientHistoryEventsTitleMarkersLayer = useClientFuelHistoryEventsTitleMarkersLayer();
  const stopTruckHistoryEventsMarkerLayer = useStopTruckHistoryEventsMarkerLayer();
  const untrackHistoryEventsMarkerLayer = useUntrackHistoryEventsMarkerLayer();
  const userCurrentLocationMarkersLayer = useUserCurrentLocationMarkersLayer();
  const historyPathPointMarkerLayer = useHistoryPathPointMarkerLayer();

  const stationMarkersLayer = useStationMarkersLayer(
    {
      data: stationsDisplay ? (stations as any) : [],
    },
    {
      onMarkerClick,
    }
  );
  const selectedStationOnRouteLayer = useSelectedStationOnRouteLayer({
    onMarkerClick,
  });
  const stationPriceMarkerLayers = useStationPriceMarkerLayers();
  const foundedPlaceLayer = useFoundedPlaceLayer({ extraStations });
  const selectedStationOnRoutePricesLayer = useSelectedStationOnRoutePricesLayer();

  // tolls
  const tollMarkerLayer = useTollMarkerLayer(activeRoute?.tolls || EMPTY_ARRAY);
  const geoUserPositionLayer = useUserGeoMarkersLayer();

  useEffect(() => {
    updateLayers([
      stationMarkersLayer,
      stationPriceMarkerLayers,
      selectedStationOnRouteLayer,
      selectedStationOnRoutePricesLayer,
      foundedPlaceLayer,
      carMarkersLayer,
      carNumberMarkersLayer,

      clientHistoryEventsMarkersLayer,
      clientHistoryEventsTitleMarkersLayer,
      stopTruckHistoryEventsMarkerLayer,
      untrackHistoryEventsMarkerLayer,
      userCurrentLocationMarkersLayer,
      historyPathPointMarkerLayer,
      tollMarkerLayer,
      geoUserPositionLayer,
      ...(layers as any),
    ]);
  }, [
    // Stations
    stationMarkersLayer,
    stationPriceMarkerLayers,
    selectedStationOnRouteLayer,
    selectedStationOnRoutePricesLayer,
    foundedPlaceLayer,
    carMarkersLayer,
    carNumberMarkersLayer,
    clientHistoryEventsMarkersLayer,
    clientHistoryEventsTitleMarkersLayer,
    stopTruckHistoryEventsMarkerLayer,
    untrackHistoryEventsMarkerLayer,
    userCurrentLocationMarkersLayer,
    historyPathPointMarkerLayer,
    tollMarkerLayer,
    geoUserPositionLayer,
    layers,
    updateLayers,
  ]);
};

export default useUpdateLayers;
